import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
    WritableSignal,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import {
    EntityAttributesInputComponent,
    EntityAttributesInputType,
} from '@connect-services/client/components/entity-attributes-input/entity-attributes-input.component';
import { AttributeValue } from '@connect-services/client/components/table-entity-detail/abstract-entity-detail.component';
import { AttributeInformationService } from '@connect-services/client/services/attribute-information.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { readOnlyAttributes } from '@connect-services/client/services/layer.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import {
    ButtonComponent,
    ButtonType,
    IconPosition,
} from '@connect-services/src/lib/components/button/button.component';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { Column, InformationSchemaTableExtended } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

export interface ExtendsAttributeValue extends AttributeValue {
    formattedValue?: string;
    label: string;
    labelTooltip?: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, EntityAttributesInputComponent, ExpansionPanelComponent, RessourcesModule],
    selector: 'app-entity-attributes',
    templateUrl: './entity-attributes.component.html',
})
export class EntityAttributesComponent {
    public readonly attributesInformations = input.required<InformationSchemaTableExtended[]>();
    public readonly bookmarkedAttributes = input.required<AttributeValue[]>();
    public readonly buttonLabel = computed(() => {
        const base = this.translocoService.translate(this.showAll() ? 'displayLess' : 'displayMore');

        return this.showAll() ? base : `${base} (${this.notBookmarkedAttributes()!.length})`;
    });
    public readonly columnNamesWithReference = input<string[]>([]);
    public readonly columnsByName = input<Map<string, Column>>(new Map());
    public readonly isLayerEditable = input<boolean>(true);
    public readonly nonEditableColumnsName = input<string[]>([]);
    public readonly nonEditableColumnsReasonByName = input<Record<string, string | null>>({});
    public readonly notBookmarkedAttributes = input.required<AttributeValue[]>();

    protected readonly formattedBookmarkedAttributes = computed<ExtendsAttributeValue[]>((): ExtendsAttributeValue[] =>
        this.bookmarkedAttributes().reduce(
            (acc: ExtendsAttributeValue[], attribute: AttributeValue): ExtendsAttributeValue[] => {
                acc.push(this.formatAttribute(attribute));

                return acc;
            },
            [],
        ),
    );
    protected readonly formattedNotBookmarkedAttributes = computed<ExtendsAttributeValue[]>(
        (): ExtendsAttributeValue[] =>
            this.notBookmarkedAttributes().reduce(
                (acc: ExtendsAttributeValue[], attribute: AttributeValue): ExtendsAttributeValue[] => {
                    acc.push(this.formatAttribute(attribute));

                    return acc;
                },
                [],
            ),
    );
    protected readonly showAll: WritableSignal<boolean> = signal(false);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly editEntityDetailService = inject(EditEntityDetailService);
    protected readonly IconPosition = IconPosition;
    protected readonly Object = Object;
    protected readonly readOnlyAttributes = readOnlyAttributes;
    protected readonly String = String;

    private readonly attributeInformationService = inject(AttributeInformationService);
    private readonly translateService = inject(TranslateService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        effect(() => {
            this.setShowAll(this.bookmarkedAttributes().length);
        });
    }

    public setShowAll(length: number): void {
        if (!length) {
            this.showAll.set(true);

            return;
        }

        this.showAll.set(false);
    }

    protected toggleList(): void {
        this.showAll.update((value) => !value);
    }

    private formatAttribute(attribute: AttributeValue): ExtendsAttributeValue {
        const attributeInfo: InformationSchemaTableExtended | null = this.attributeInformationService.findByKey(
            this.attributesInformations(),
            attribute.key,
        );
        const alias: string | null | undefined = this.columnsByName().get(attribute.key)?.alias;

        let formattedValue: string = String(attribute.value);

        if (attributeInfo && typeof attribute.value === 'string') {
            switch (attributeInfo.dataType) {
                case EntityAttributesInputType.TIMESTAMPTZ:
                    formattedValue = this.translateService.getFormattedLocaleDate(
                        new Date(attribute.value),
                        this.translateService.getDateHourFormat(),
                    );
                    break;
                case EntityAttributesInputType.DATE:
                    formattedValue = this.translateService.getFormattedLocaleDate(new Date(attribute.value));
                    break;
            }
        }

        return {
            ...attribute,
            formattedValue: formattedValue,
            label: alias ?? attribute.key,
            labelTooltip: alias ? attribute.key : undefined,
        };
    }
}
